import * as pdfjs from 'pdfjs-dist/build/pdf.mjs';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const fields = ['name', 'client', 'email', 'type', 'date', 'venue'];

const aliases = {
  name: [
    'event name',
    'event title',
    'booking name',
    'booking title',
    'occasion name',
    'invoice name',
    'order name',
    'event',
    'booking',
    'occasion',
    'product name',
    'product',
    'item name',
    'item',
    'service name',
    'service',
    'description'
  ],
  client: [
    'client name',
    'customer name',
    'billing name',
    'bill to',
    'customer',
    'attendee',
    'recipient',
    'name'
  ],
  email: [
    'billing email',
    'customer email',
    'client email',
    'email address',
    'email'
  ],
  type: [
    'event type',
    'booking type',
    'occasion type',
    'event category',
    'booking category',
    'category',
    'occasion'
  ],
  date: [
    'event date',
    'wedding date',
    'booking date',
    'party date',
    'date of event',
    'function date',
    'event day'
  ],
  venue: [
    'event venue',
    'reception venue',
    'event location',
    'venue',
    'location',
    'event address',
    'address of event'
  ]
};

const eventTypes = [
  {
    type: 'Wedding',
    patterns: [
      'wedding',
      'bride and groom',
      'bride',
      'groom',
      'ceremony',
      'reception'
    ]
  },
  {
    type: 'Bar/Bat Mitzvah',
    patterns: [
      'bar mitzvah',
      'bat mitzvah',
      'b mitzvah',
      'mitzvah'
    ]
  },
  {
    type: 'Corporate',
    patterns: [
      'corporate',
      'company event',
      'conference',
      'business event',
      'holiday party',
      'awards dinner'
    ]
  },
  {
    type: 'Birthday',
    patterns: [
      'birthday',
      'sweet sixteen',
      'sweet 16',
      'quinceañera',
      'quinceanera'
    ]
  }
];

const ignoredValues = new Set([
  'invoice',
  'invoice number',
  'order',
  'order number',
  'receipt',
  'customer',
  'billing',
  'shipping',
  'subtotal',
  'total',
  'tax',
  'payment method',
  'quantity',
  'description',
  'details',
  'price',
  'amount',
  'product',
  'item'
]);

const stopLabels = [
  'subtotal',
  'shipping',
  'tax',
  'total',
  'payment method',
  'order notes',
  'customer notes',
  'billing address',
  'shipping address'
];

const nonEventDateLabels = [
  'invoice date',
  'order date',
  'created date',
  'date created',
  'due date',
  'payment date',
  'paid date'
];

function clean(value = '') {
  return String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\\n/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^["']|["']$/g, '')
    .trim();
}

function normalizeKey(value = '') {
  return clean(value)
    .toLowerCase()
    .replace(/[_/#()|-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isEmail(value) {
  return /[^\s<>,;()[\]]+@[^\s<>,;()[\]]+\.[^\s<>,;()[\]]+/.test(
    value
  );
}

function isPhone(value) {
  return /(?:\+?1[\s.-]?)?\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/.test(
    value
  );
}

function isMoney(value) {
  return /^(?:[$£€]\s*)?\d+(?:[,.]\d{2})?$/.test(
    clean(value).replace(/\s/g, '')
  );
}

function normalizeDate(value = '') {
  const text = clean(value);
  if (!text) return '';

  const iso = text.match(
    /\b(\d{4})[-/](\d{1,2})[-/](\d{1,2})\b/
  );

  if (iso) {
    const [, year, month, day] = iso;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const numeric = text.match(
    /\b(\d{1,2})[-/](\d{1,2})[-/](\d{4})\b/
  );

  if (numeric) {
    const [, first, second, year] = numeric;
    const month = Number(first) > 12 ? second : first;
    const day = Number(first) > 12 ? first : second;

    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  const monthMatch = text.match(
    /\b(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+(\d{1,2})(?:,|\s)+(\d{4})\b/i
  );

  if (!monthMatch) return '';

  const [, monthName, day, year] = monthMatch;
  const month = new Date(`${monthName} 1, ${year}`).getMonth() + 1;

  return `${year}-${String(month).padStart(2, '0')}-${day.padStart(
    2,
    '0'
  )}`;
}

function normalizeField(field, value) {
  const text = clean(value);

  if (field === 'date') return normalizeDate(text);

  if (field === 'email') {
    return (
      text.match(
        /[^\s<>,;()[\]]+@[^\s<>,;()[\]]+\.[^\s<>,;()[\]]+/
      )?.[0] || ''
    );
  }

  return text;
}

function isUseful(field, value) {
  const normalized = normalizeField(field, value);

  if (!normalized) return false;
  if (ignoredValues.has(normalizeKey(normalized))) return false;
  if (field === 'email' && !isEmail(normalized)) return false;
  if (field === 'date' && !normalizeDate(normalized)) return false;

  return true;
}

function makeEntries(lines) {
  const entries = [];

  lines.forEach((line, index) => {
    const current = clean(line);
    const next = clean(lines[index + 1] || '');

    if (!current) return;

    const labeled = current.match(
      /^([^:;|=]{2,140})\s*[:;|=]\s*(.+)$/
    );

    if (labeled) {
      entries.push([labeled[1], labeled[2]]);
      return;
    }

    const inline = current.match(
      /^(.*?(?:name|email|date|venue|location|address|occasion|event|customer|billing|order|product|service|type|category|details))\s+(.+)$/i
    );

    if (inline && inline[2].length > 1) {
      entries.push([inline[1], inline[2]]);
      return;
    }

    if (
      next &&
      current.length <= 100 &&
      !current.includes('@') &&
      !/\d{3,}/.test(current) &&
      /^[A-Za-z][A-Za-zÀ-ÿ &'/.(),-]*$/.test(current)
    ) {
      entries.push([current, next]);
    }
  });

  return entries;
}

function findEntry(entries, field) {
  const fieldAliases = aliases[field].map(normalizeKey);

  return (
    entries
      .map(([label, value]) => ({
        label: normalizeKey(label),
        value: normalizeField(field, value)
      }))
      .filter(({label, value}) => {
        const matches = fieldAliases.some(
          (alias) =>
            label === alias ||
            label.endsWith(` ${alias}`) ||
            label.includes(alias)
        );

        return matches && isUseful(field, value);
      })
      .sort((first, second) => {
        const firstExact = fieldAliases.includes(first.label) ? 1 : 0;
        const secondExact = fieldAliases.includes(second.label) ? 1 : 0;

        return (
          secondExact - firstExact ||
          second.label.length - first.label.length
        );
      })[0]?.value || ''
  );
}

function isSectionHeading(line) {
  return /^(bill(?:ing)?\s+to|billing details|customer details|client details|ship(?:ping)?\s+to|customer)$/i.test(
    clean(line)
  );
}

function isLikelyPersonName(value) {
  const text = clean(value);
  const normalized = normalizeKey(text);

  if (!text || isEmail(text) || isPhone(text)) return false;
  if (isMoney(text) || /^\d+$/.test(text)) return false;
  if (text.length < 2 || text.length > 90) return false;
  if (stopLabels.includes(normalized)) return false;
  if (/^(phone|email|address|city|state|zip|country)\b/i.test(text)) {
    return false;
  }

  return /^[A-Za-zÀ-ÿ][A-Za-zÀ-ÿ .,'&-]*$/.test(text);
}

function findClient(lines, entries) {
  const labeledClient = findEntry(entries, 'client');

  if (
    labeledClient &&
    !isEmail(labeledClient) &&
    isLikelyPersonName(labeledClient)
  ) {
    return labeledClient;
  }

  const sectionIndexes = lines
    .map((line, index) => (isSectionHeading(line) ? index : -1))
    .filter((index) => index >= 0);

  for (const sectionIndex of sectionIndexes) {
    const candidate = lines
      .slice(sectionIndex + 1, sectionIndex + 8)
      .map(clean)
      .find(isLikelyPersonName);

    if (candidate) return candidate;
  }

  const emailIndex = lines.findIndex((line) => isEmail(line));

  if (emailIndex > 0) {
    const candidate = lines
      .slice(Math.max(0, emailIndex - 4), emailIndex)
      .reverse()
      .find(isLikelyPersonName);

    if (candidate) return candidate;
  }

  return '';
}

function findType(value = '') {
  const normalized = normalizeKey(value);

  for (const eventType of eventTypes) {
    if (
      eventType.patterns.some((pattern) =>
        normalized.includes(normalizeKey(pattern))
      )
    ) {
      return eventType.type;
    }
  }

  return '';
}

function findExplicitType(entries) {
  const typeEntry = findEntry(entries, 'type');

  if (!typeEntry) return '';

  return findType(typeEntry) || typeEntry;
}

function removeInvoiceMetadata(value) {
  return clean(value)
    .replace(/\.(pdf|csv|json|txt)$/i, '')
    .replace(/\b(invoice|order|receipt|woocommerce)\b/gi, ' ')
    .replace(/(?:#|no\.?|number)\s*[\w-]+/gi, ' ')
    .replace(/\b(?:event|booking|occasion)\s*[:#-]?\s*/i, '')
    .replace(/\s+/g, ' ')
    .replace(/^[|:/,_-]+|[|:/,_-]+$/g, '')
    .trim();
}

function cleanEventName(value, client = '') {
  const name = removeInvoiceMetadata(value);

  if (!name || ignoredValues.has(normalizeKey(name))) return '';
  if (client && normalizeKey(name) === normalizeKey(client)) return '';
  if (isEmail(name) || isPhone(name) || isMoney(name)) return '';

  return name.length >= 3 && name.length <= 160 ? name : '';
}

function isLikelyEventName(value, client = '') {
  const name = cleanEventName(value, client);

  if (!name || !/[A-Za-zÀ-ÿ]/.test(name)) return false;
  if (/^(bill|billing|ship|shipping|customer|client|date|venue|email|phone)\b/i.test(name)) {
    return false;
  }

  return true;
}

function candidateName(value, client, source) {
  const name = cleanEventName(value, client);
  if (!isLikelyEventName(name, client)) return null;

  const normalized = normalizeKey(name);
  const normalizedSource = normalizeKey(source);
  let score = 0;

  if (findType(name)) score += 5;
  if (normalizedSource.includes(normalized)) score += 2;
  if (/\b(event|booking|occasion|wedding|mitzvah|birthday|corporate)\b/i.test(name)) {
    score += 3;
  }

  if (name.split(' ').length >= 2) score += 1;

  return {name, score};
}

function findName(lines, entries, client, fileName, source) {
  const candidates = [];

  const addCandidate = (value, bonus = 0) => {
    const candidate = candidateName(value, client, source);

    if (candidate) {
      candidates.push({
        ...candidate,
        score: candidate.score + bonus
      });
    }
  };

  addCandidate(findEntry(entries, 'name'), 10);

  const detailLabels = [
    'event details',
    'booking details',
    'order details',
    'invoice details',
    'event information',
    'booking information',
    'additional details',
    'order notes',
    'customer notes'
  ];

  lines.forEach((line, index) => {
    const normalized = normalizeKey(line);
    const isDetailsLine = detailLabels.some(
      (label) =>
        normalized === label ||
        normalized.startsWith(`${label} `)
    );

    if (isDetailsLine) {
      lines
        .slice(index + 1, index + 9)
        .forEach((detailLine) => addCandidate(detailLine, 8));
    }
  });

  const productHeaders = /^(product|products|item|items|service|services|description)$/i;

  lines.forEach((line, index) => {
    if (!productHeaders.test(clean(line))) return;

    lines
      .slice(index + 1, index + 10)
      .forEach((productLine) => {
        if (!stopLabels.some((label) =>
          normalizeKey(productLine).startsWith(label)
        )) {
          addCandidate(productLine, 9);
        }
      });
  });

  lines.forEach((line) => {
    const match = line.match(
      /(?:event|booking|occasion)\s*(?:name|title)?\s*[:=]\s*(.+)$/i
    );

    if (match) addCandidate(match[1], 12);
  });

  const filenameParts = fileName
    .replace(/\.(pdf|csv|json|txt)$/i, '')
    .split(/[-_|,]+/)
    .map(clean)
    .filter(Boolean);

  filenameParts.forEach((part) => addCandidate(part, findType(part) ? 14 : 5));

  const firstLines = lines.slice(0, 18);

  firstLines.forEach((line) => {
    const normalized = normalizeKey(line);

    if (
      !normalized ||
      normalized.startsWith('bill ') ||
      normalized.startsWith('billing ') ||
      normalized.startsWith('ship ') ||
      normalized.startsWith('shipping ') ||
      normalized.startsWith('customer ') ||
      normalized.startsWith('invoice ') ||
      normalized.startsWith('order ') ||
      normalized.includes('@')
    ) {
      return;
    }

    addCandidate(line, findType(line) ? 7 : 1);
  });

  const best = candidates
    .sort((first, second) => second.score - first.score)
    .find((candidate, index, all) => {
      return !all
        .slice(0, index)
        .some(
          (previous) =>
            normalizeKey(previous.name) === normalizeKey(candidate.name)
        );
    });

  return best?.name || (client ? `${client} Event` : '');
}

function findDate(lines, entries, source) {
  const labeledDate = findEntry(entries, 'date');

  if (labeledDate) return labeledDate;

  const datePattern =
    /\b(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\s+\d{1,2}(?:,|\s)+\d{4}\b|\b\d{4}[-/]\d{1,2}[-/]\d{1,2}\b|\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/gi;

  for (const line of lines) {
    const normalized = normalizeKey(line);

    if (
      nonEventDateLabels.some((label) =>
        normalized.includes(normalizeKey(label))
      )
    ) {
      continue;
    }

    const date = normalizeDate(line.match(datePattern)?.[0] || '');

    if (date) return date;
  }

  return normalizeDate(source.match(datePattern)?.[0] || '');
}

function findVenue(lines, entries) {
  const labeledVenue = findEntry(entries, 'venue');

  if (labeledVenue) return labeledVenue;

  const venueIndex = lines.findIndex((line) =>
    /^(venue|event location|event address|reception venue)$/i.test(
      clean(line)
    )
  );

  return venueIndex >= 0 ? clean(lines[venueIndex + 1] || '') : '';
}

function extractPdfLines(items) {
  const rows = new Map();

  items.forEach((item) => {
    const text = clean(item.str || '');
    if (!text) return;

    const x = item.transform?.[4] || 0;
    const y = Math.round(item.transform?.[5] || 0);
    const row = rows.get(y) || [];

    row.push({x, text});
    rows.set(y, row);
  });

  return [...rows.entries()]
    .sort(([first], [second]) => second - first)
    .map(([, row]) =>
      row
        .sort((first, second) => first.x - second.x)
        .map((item) => item.text)
        .join(' ')
    )
    .map(clean)
    .filter(Boolean);
}

async function extractPdfText(file) {
  const data = new Uint8Array(await file.arrayBuffer());
  const document = await pdfjs.getDocument({data}).promise;
  const pages = [];

  for (
    let pageNumber = 1;
    pageNumber <= document.numPages;
    pageNumber += 1
  ) {
    const page = await document.getPage(pageNumber);
    const content = await page.getTextContent();

    pages.push(extractPdfLines(content.items).join('\n'));
  }

  return pages.join('\n');
}

function parseStructuredText(source, fileName = '') {
  const lines = source.split(/\r?\n/).map(clean).filter(Boolean);
  const entries = makeEntries(lines);
  const client = findClient(lines, entries);
  const explicitType = findExplicitType(entries);
  const name = findName(lines, entries, client, fileName, source);

  return {
    name,
    client,
    email:
      findEntry(entries, 'email') ||
      source.match(
        /[^\s<>,;()[\]]+@[^\s<>,;()[\]]+\.[^\s<>,;()[\]]+/
      )?.[0] ||
      '',
    type: explicitType || findType(`${name}\n${source}`),
    date: findDate(lines, entries, source),
    venue: findVenue(lines, entries)
  };
}

function parseCsv(source, fileName) {
  const rows = source
    .split(/\r?\n/)
    .map((row) => row.split(',').map(clean))
    .filter((row) => row.some(Boolean));

  if (rows.length < 2) return parseStructuredText(source, fileName);

  const headers = rows[0].map(normalizeKey);
  const values = rows[1];

  const parsed = Object.fromEntries(
    fields.map((field) => {
      const index = aliases[field]
        .map(normalizeKey)
        .reduce((found, alias) => {
          if (found >= 0) return found;

          return headers.findIndex(
            (header) => header === alias || header.includes(alias)
          );
        }, -1);

      return [field, index >= 0 ? values[index] || '' : ''];
    })
  );

  const structured = parseStructuredText(source, fileName);

  return {
    ...structured,
    ...Object.fromEntries(
      fields.map((field) => [
        field,
        parsed[field] || structured[field] || ''
      ])
    )
  };
}

function parseJson(source, fileName) {
  try {
    const parsed = JSON.parse(source);
    const flattened = [];

    const flatten = (value, prefix = '') => {
      if (Array.isArray(value)) {
        value.forEach((item, index) =>
          flatten(item, `${prefix} ${index}`)
        );
        return;
      }

      if (value && typeof value === 'object') {
        Object.entries(value).forEach(([key, child]) => {
          flatten(child, prefix ? `${prefix} ${key}` : key);
        });
        return;
      }

      if (value !== null && value !== undefined) {
        flattened.push(`${prefix}: ${value}`);
      }
    };

    flatten(parsed);

    return parseStructuredText(flattened.join('\n'), fileName);
  } catch {
    return parseStructuredText(source, fileName);
  }
}

export async function parseWooCommerceInvoice(file) {
  const extension = file.name.split('.').pop()?.toLowerCase();
  let source = '';
  let parsed = {};

  if (extension === 'pdf') {
    source = await extractPdfText(file);
    parsed = parseStructuredText(source, file.name);
  } else if (extension === 'csv') {
    source = await file.text();
    parsed = parseCsv(source, file.name);
  } else if (extension === 'json') {
    source = await file.text();
    parsed = parseJson(source, file.name);
  } else {
    source = await file.text();
    parsed = parseStructuredText(source, file.name);
  }

  return Object.fromEntries(
    fields.map((field) => [field, clean(parsed[field] || '')])
  );
}